import { Fragment, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { NavLink, Outlet, useLocation } from "react-router";
import { useQuery } from "@tanstack/react-query";
import { api, till } from "@/lib/api";
import { useSession, OFFICE_ROLE_LABEL, type OfficeRole } from "@/lib/session";
import { useShop, useTill } from "@/lib/till-context";
import { atLeast, type Item } from "@/lib/types";
import { mediaUrl } from "@/lib/media";
import { Avatar, cx } from "@/components/ui";
import { Icon } from "@/components/Icon";
import { useInstall } from "@/lib/install";
import { session } from "@/lib/session";
import { StockTabs, isStockPage } from "./StockTabs";
import { PeopleTabs, isPeoplePage } from "./PeopleTabs";
import { useWaitingRefunds } from "@/pages/Refunds";

/** Settings is split into smaller pages, shown as links under it in the menu. */
export const SETTINGS_LINKS = [
  { to: "/settings/tills", label: "Shops & tills", icon: "till" },
  { to: "/settings/selling", label: "What you sell", icon: "box" },
  { to: "/settings/limits", label: "Limits & returns", icon: "lock" },
  { to: "/settings/payments", label: "Payments & offline", icon: "cash" },
  { to: "/settings/delivery", label: "Delivery", icon: "truck" },
  { to: "/settings/receipt", label: "Receipt & screen", icon: "printer" },
];

type NavItem = { to: string; label: string; icon: string; min?: "manager"; subs?: { to: string; label: string; icon?: string }[]; count?: "products" | "refunds"; /** Back office roles that see it; none = everyone. */ roles?: OfficeRole[] };
const STOCK_PAGES: NavItem[] = [{ to: "/stock", label: "Receive", icon: "truck", roles: ["owner", "gm", "manager", "stock"] }, { to: "/count", label: "Count", icon: "list", roles: ["owner", "gm", "manager", "stock"] }, { to: "/log", label: "Log", icon: "report" }, { to: "/health", label: "Health", icon: "pulse" }, { to: "/catalog", label: "Categories, brands & units", icon: "list", roles: ["owner", "gm", "manager", "stock"] }, { to: "/suppliers", label: "Suppliers", icon: "truck", roles: ["owner", "gm", "manager", "stock", "accountant"] }];
/** Pages inside Staff & roles (Buildwise): reached by its tabs, not the side menu. */
const PEOPLE_PAGES: NavItem[] = [{ to: "/team", label: "Back office team", icon: "team", roles: ["owner", "gm"] }, { to: "/permissions", label: "Roles & permissions", icon: "lock", roles: ["owner", "gm"] }];
const BRAND_STOCK: NavItem[] = [{ to: "/products", label: "Products & stock", icon: "box", count: "products" }];
const DISCOUNTS: NavItem = { to: "/discounts", label: "Discounts", icon: "tag", roles: ["owner", "gm", "manager"] };
const REFUNDS: NavItem = { to: "/refunds", label: "Refunds", icon: "left", count: "refunds" };
const GROUPS: { label: string; items: NavItem[] }[] = import.meta.env.VITE_BRAND ? [
  { label: "Overview", items: [{ to: "/", label: "Dashboard", icon: "dash" }, { to: "/reports", label: "Reports", icon: "report" }] },
  { label: "Stock", items: BRAND_STOCK },
  { label: "Money", items: [{ to: "/cash", label: "Cash control", icon: "cash", roles: ["owner", "gm", "manager", "accountant"] }, REFUNDS, DISCOUNTS] },
  { label: "Customers", items: [{ to: "/customers", label: "Customers & groups", icon: "team", roles: ["owner", "gm", "manager", "accountant"] }, { to: "/owed", label: "Money owed", icon: "cash", roles: ["owner", "gm", "manager", "accountant"] }] },
  { label: "Team & setup", items: [{ to: "/staff", label: "Staff & roles", icon: "team", roles: ["owner", "gm", "manager"] }, { to: "/activity", label: "Activity log", icon: "list", roles: ["owner", "gm", "manager"] }, { to: "/settings", label: "Settings", icon: "gear", roles: ["owner", "gm", "manager"], subs: SETTINGS_LINKS }] },
] : [
  { label: "Overview", items: [{ to: "/", label: "Dashboard", icon: "dash" }, { to: "/reports", label: "Reports", icon: "report" }] },
  { label: "Stock", items: [{ to: "/products", label: "Products & stock", icon: "box", count: "products" }, { to: "/health", label: "Stock health", icon: "pulse" }, { to: "/catalog", label: import.meta.env.VITE_BRAND ? "Categories, brands & units" : "Categories & brands", icon: "list", roles: ["owner", "gm", "manager", "stock"] }, ...(import.meta.env.VITE_BRAND ? [{ to: "/suppliers", label: "Suppliers", icon: "truck", roles: ["owner", "gm", "manager", "stock", "accountant"] as OfficeRole[] }] : []), { to: "/discounts", label: "Discounts", icon: "tag", roles: ["owner", "gm", "manager"] }, ...(import.meta.env.VITE_BRAND ? [{ to: "/stock", label: "Receive stock", icon: "truck", roles: ["owner", "gm", "manager", "stock"] as OfficeRole[] }, { to: "/count", label: "Count stock", icon: "list", roles: ["owner", "gm", "manager", "stock"] as OfficeRole[] }, { to: "/log", label: "Stock log", icon: "report" }] : [{ to: "/stock", label: "Receive & transfer", icon: "truck", roles: ["owner", "gm", "manager", "stock"] as OfficeRole[] }])] },
  { label: "Money", items: [{ to: "/cash", label: "Cash control", icon: "cash", roles: ["owner", "gm", "manager", "accountant"] }] },
  { label: "Customers", items: [{ to: "/customers", label: "Customers & groups", icon: "team", roles: ["owner", "gm", "manager", "accountant"] }, { to: "/owed", label: "Money owed", icon: "cash", roles: ["owner", "gm", "manager", "accountant"] }] },
  { label: "Team & setup", items: [{ to: "/staff", label: "Staff & roles", icon: "team", roles: ["owner", "gm", "manager"] }, { to: "/team", label: "Team (back office)", icon: "team", roles: ["owner", "gm"] }, { to: "/permissions", label: "Roles & permissions", icon: "lock", roles: ["owner", "gm"] }, { to: "/activity", label: "Activity log", icon: "list", roles: ["owner", "gm", "manager"] }, { to: "/settings", label: "Settings", icon: "gear", roles: ["owner", "gm", "manager"], subs: SETTINGS_LINKS }] },
];
/** The permission each screen depends on; the menu follows what's ticked on Roles & permissions. */
const AREA_OF: Record<string, string> = { "/catalog": "products", "/discounts": "discounts", "/stock": "stock", "/count": "stock", "/cash": "cash", "/customers": "money", "/owed": "money", "/staff": "staff", "/team": "team", "/permissions": "team", "/settings": "settings" };
const TITLES: Record<string, string> = { "/": "Dashboard", "/reports": "Reports", "/products": "Products & stock", "/stock": import.meta.env.VITE_BRAND ? "Receive stock" : "Receive & transfer", "/count": "Count stock", "/log": "Stock log", "/customers": "Customers & groups", "/staff": "Staff & roles", "/team": "Team", "/owed": "Money owed", "/health": "Stock health","/activity": "Activity log", "/settings": "Settings", ...Object.fromEntries(SETTINGS_LINKS.map((l) => [l.to, l.label])), "/catalog": import.meta.env.VITE_BRAND ? "Categories, brands & units" : "Categories & brands", "/suppliers": "Suppliers", "/discounts": "Discounts", "/refunds": "Refunds", "/cash": "Cash control", "/permissions": "Roles & permissions" };

/**
 * The back office shell (its own site, signed in with a store admin account). Wide screens: full sidebar. Tablets (md to xl): an icon strip.
 * Phones: a top bar with a slide-out menu.
 */
/** What this person can open: follows Roles & permissions, like the side menu. */
function useAllowed() {
  const s = useSession();
  const { me } = useTill();
  const role: OfficeRole = me?.user.role ?? s.user?.role ?? "owner";
  const all = [...GROUPS.flatMap((g) => g.items), ...STOCK_PAGES, ...(import.meta.env.VITE_BRAND ? PEOPLE_PAGES : [])];
  return (to: string) => { const i = all.find((x) => x.to === to); return !!i && (AREA_OF[to] && me?.can ? !!me.can[AREA_OF[to]!] : !i.roles || i.roles.includes(role)); };
}

/** Buildwise on a phone: the main pages in a bottom menu, the rest under More. Follows the same permissions as the side menu. */
function BottomNav({ onMore, moreOpen }: { onMore: () => void; moreOpen: boolean }) {
  const s = useSession();
  const { me } = useTill();
  const role: OfficeRole = me?.user.role ?? s.user?.role ?? "owner";
  const path = useLocation().pathname;
  const stockPage = isStockPage(path);
  const allowed = useAllowed();
  void role;
  const tabs = [{ to: "/", label: "Home", icon: "dash" }, { to: "/products", label: "Stock", icon: "box" }, { to: "/cash", label: "Cash", icon: "cash" }, { to: "/reports", label: "Reports", icon: "report" }].filter((t) => allowed(t.to));
  const box = "flex h-full min-w-0 flex-1 flex-col items-center justify-center gap-0.5 rounded-[18px] text-[11px] font-bold";
  return (
    <nav aria-label="Main" className="ui-bottomnav fixed inset-x-3 bottom-3 z-40 flex h-[68px] gap-1 rounded-3xl bg-white p-1.5 shadow-[0_10px_30px_rgba(11,21,23,0.18)] md:hidden print:hidden">
      {tabs.map((t) => (
        <NavLink key={t.to} to={t.to} end={t.to === "/"} className={({ isActive }) => cx(box, (isActive || (t.to === "/products" && stockPage)) && !moreOpen ? "bg-brand-tint text-brand-ink" : "text-slate-600")}><Icon name={t.icon} size={21} />{t.label}</NavLink>
      ))}
      <button type="button" onClick={onMore} aria-expanded={moreOpen} className={cx(box, moreOpen ? "bg-brand-tint text-brand-ink" : "text-slate-600")}><Icon name="menu" size={21} />More</button>
    </nav>
  );
}

/** Tables read as cards on a phone: each cell is labelled from its column heading (see index.css). */
function useTableLabels(on: boolean) {
  const box = useRef<HTMLElement>(null);
  useEffect(() => {
    if (!on || !box.current) return;
    const root = box.current;
    let raf = 0;
    const run = () => {
      root.querySelectorAll("table").forEach((table) => {
        // Long rows fold to their key facts; tap to open. Rows being filled in stay open.
        const heads = [...table.querySelectorAll("thead th")].map((th) => (th.querySelector(".sr-only") ? "" : (th.textContent ?? "").trim()));
        table.querySelectorAll("tbody tr").forEach((tr) => {
          const cells = [...tr.children];
          cells.forEach((td, i) => { if (!td.hasAttribute("colspan")) td.setAttribute("data-label", heads[i] ?? ""); });
          if (tr.querySelector("td[colspan]") || tr.querySelector("input, [role=switch], [role=combobox]")) return;
          const right = cells.filter((td) => td.className.includes("text-right") && !td.querySelector("button, a") && (td.textContent ?? "").trim());
          const key = right.find((td) => td.className.includes("font-bold")) ?? right[right.length - 1];
          cells.forEach((td) => td.toggleAttribute("data-key", td === key));
          tr.toggleAttribute("data-fold", cells.length > 2);
          tr.toggleAttribute("data-opens", tr.className.includes("cursor-pointer"));
        });
      });
    };
    run();
    const tap = (e: Event) => {
      const el = e.target as HTMLElement;
      const tr = el.closest("tr[data-fold]:not([data-opens])");
      if (tr && !el.closest("a, button, input, select")) tr.toggleAttribute("data-open");
    };
    root.addEventListener("click", tap);
    const seen = new MutationObserver(() => { cancelAnimationFrame(raf); raf = requestAnimationFrame(run); });
    seen.observe(root, { childList: true, subtree: true });
    return () => { seen.disconnect(); root.removeEventListener("click", tap); cancelAnimationFrame(raf); };
  }, [on]);
  return box;
}

/** Buildwise on a computer: where you are on the left; install, date and your account on the right. */
function TopBar({ folded, onToggle }: { folded: boolean; onToggle: () => void }) {
  const path = useLocation().pathname;
  const s = useSession();
  const { me } = useTill();
  const role: OfficeRole = me?.user.role ?? s.user?.role ?? "owner";
  const app = useInstall();
  const [open, setOpen] = useState(false);
  const box = useRef<HTMLDivElement>(null);
  useEffect(() => setOpen(false), [path]);
  useEffect(() => {
    if (!open) return;
    const away = (e: MouseEvent) => { if (!box.current?.contains(e.target as Node)) setOpen(false); };
    const esc = (e: KeyboardEvent) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", away);
    document.addEventListener("keydown", esc);
    return () => { document.removeEventListener("mousedown", away); document.removeEventListener("keydown", esc); };
  }, [open]);
  const base = "/" + (path.split("/")[1] ?? "");
  const group = GROUPS.find((g) => g.items.some((i) => i.to === base || (i.to === "/products" && isStockPage(path)) || (i.to === "/staff" && isPeoplePage(path))))?.label;
  const page = TITLES[path] ?? TITLES[base] ?? "Back office";
  const roleText = me?.user.roleLabel ?? OFFICE_ROLE_LABEL[role];
  const today = new Date().toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long" });
  return (
    <header className="sticky top-0 z-30 hidden h-16 flex-none items-center gap-3 border-b border-slate-200 bg-white/90 px-4 backdrop-blur md:flex lg:px-6 print:hidden">
      <button type="button" onClick={onToggle} aria-label={folded ? "Show the full menu" : "Fold the menu to icons"} title={folded ? "Show the full menu" : "Fold the menu"} aria-pressed={!folded} className="grid h-10 w-10 flex-none place-items-center rounded-xl border border-slate-200 bg-white text-slate-600 shadow-sm transition-colors hover:border-slate-300 hover:bg-slate-50 hover:text-ink">
        <Icon name="panel" size={19} />
      </button>
      <span className="h-6 w-px flex-none bg-slate-200" />
      <nav aria-label="You are here" className="flex min-w-0 flex-1 items-center gap-2 text-sm">
        {group ? <><span className="truncate text-slate-500">{group}</span><Icon name="chev" size={14} className="flex-none text-slate-400" /></> : null}
        <b className="truncate font-bold text-ink">{page}</b>
      </nav>
      <span className="hidden flex-none text-[13px] text-slate-500 lg:block">{today}</span>
      {app.canPrompt ? (
        <button type="button" onClick={() => void app.install()} title="Install the back office as an app" className="flex h-10 flex-none items-center gap-2 rounded-xl px-3 text-[13px] font-semibold text-slate-600 hover:bg-slate-100 hover:text-ink">
          <Icon name="download" size={17} /><span className="hidden lg:inline">Install app</span>
        </button>
      ) : null}
      <span className="h-6 w-px flex-none bg-slate-200" />
      <div ref={box} className="relative flex-none">
        <button type="button" aria-haspopup="menu" aria-expanded={open} onClick={() => setOpen((v) => !v)} className={cx("flex h-11 items-center gap-2.5 rounded-xl py-1 pl-1.5 pr-2.5 hover:bg-slate-100", open && "bg-slate-100")}>
          <Avatar name={s.user?.name ?? "?"} color="#9fe0e3" size={32} />
          <span className="flex max-w-[180px] flex-col text-left leading-tight">
            <b className="truncate text-[13px]">{s.user?.name}</b>
            <span className="truncate text-[11.5px] text-slate-500">{roleText}</span>
          </span>
          <Icon name="chev" size={14} className={cx("flex-none text-slate-400 transition-transform", open ? "-rotate-90" : "rotate-90")} />
        </button>
        {open ? (
          <div role="menu" className="absolute right-0 top-[calc(100%+8px)] w-72 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-[0_16px_40px_rgba(11,21,23,0.16)]">
            <div className="flex items-center gap-3 border-b border-slate-100 p-4">
              <Avatar name={s.user?.name ?? "?"} color="#9fe0e3" size={40} />
              <span className="min-w-0">
                <b className="block break-words text-sm">{s.user?.name}</b>
                <span className="block break-all text-xs text-slate-500">{s.user?.email}</span>
                <span className="mt-1 inline-block rounded-full bg-brand-tint px-2 py-0.5 text-[11px] font-bold text-brand-ink">{roleText}</span>
              </span>
            </div>
            <button type="button" role="menuitem" onClick={() => session.signOut()} className="flex h-12 w-full items-center gap-2.5 px-4 text-sm font-semibold text-stop hover:bg-slate-50">
              <Icon name="logout" size={17} /> Log out
            </button>
          </div>
        ) : null}
      </div>
    </header>
  );
}

const FOLD_KEY = "office.menu-folded";
/** Buildwise on a computer: the side menu full or folded to icons, remembered on this device. */
function useFolded() {
  const [folded, setFolded] = useState(() => {
    try {
      const v = localStorage.getItem(FOLD_KEY);
      if (v != null) return v === "1";
    } catch {
      /* no storage */
    }
    return typeof window !== "undefined" && window.innerWidth < 1280;
  });
  const toggle = () => setFolded((v) => {
    try {
      localStorage.setItem(FOLD_KEY, v ? "0" : "1");
    } catch {
      /* still works for this visit */
    }
    return !v;
  });
  return [folded, toggle] as const;
}

export function Layout() {
  const loc = useLocation();
  const [folded, toggleFold] = useFolded();
  const [drawer, setDrawer] = useState(false);
  useEffect(() => setDrawer(false), [loc.pathname]);
  const plain = !!import.meta.env.VITE_BRAND;
  const mainBox = useTableLabels(plain);
  const allowed = useAllowed();
  const inStock = plain && isStockPage(loc.pathname);
  const inPeople = plain && isPeoplePage(loc.pathname);
  return (
    <div className="flex min-h-full bg-slate-50">
      {/* The panel colour runs the full page; the menu itself stays locked to the screen. */}
      <div className="ui-side hidden flex-none bg-graphite md:block">
      <aside className="sticky top-0 flex h-dvh overflow-hidden">
        {plain ? (
          <div className="h-full min-h-0"><Sidebar variant={folded ? "rail" : "full"} /></div>
        ) : <>
        <div className="h-full min-h-0 xl:hidden">
          <Sidebar variant="rail" />
        </div>
        <div className="hidden h-full min-h-0 xl:block">
          <Sidebar variant="full" />
        </div>
        </>}
      </aside>
      </div>
      <div className="flex min-w-0 flex-1 flex-col">
        <header className={cx("sticky top-0 z-30 h-14 items-center gap-2 bg-slate-900 px-2 text-white md:hidden", plain ? "hidden" : "flex")}>
          {plain ? null : <button type="button" aria-label="Open menu" onClick={() => setDrawer(true)} className="grid h-11 w-11 place-items-center">
            <Icon name="menu" size={20} />
          </button>}
          <b className={cx("min-w-0 flex-1 truncate text-[15px]", plain && "px-2")}>{TITLES[loc.pathname] ?? "Back office"}</b>
        </header>
        {plain ? <TopBar folded={folded} onToggle={toggleFold} /> : null}
        <main ref={mainBox} className={cx("min-w-0 flex-1 p-4 sm:p-6 lg:p-8", plain && "pb-28 md:pb-6 lg:pb-8")}>
          <div className="mx-auto w-full max-w-[1200px]">
            {inStock ? <StockTabs allowed={allowed} /> : null}
            {inPeople ? <PeopleTabs allowed={allowed} /> : null}
            <Outlet />
          </div>
        </main>
      </div>
      {plain ? <BottomNav moreOpen={drawer} onMore={() => setDrawer(!drawer)} /> : null}
      {drawer ? (
        <div className="fixed inset-0 z-30 md:hidden" role="dialog" aria-modal="true">
          <button type="button" aria-label="Close menu" onClick={() => setDrawer(false)} className="absolute inset-0 bg-slate-900/50" />
          <Sidebar variant="drawer" className={plain ? "ui-morepage absolute inset-0 w-full pb-24" : "absolute inset-y-0 left-0 w-[86vw] max-w-[320px]"} onClose={() => setDrawer(false)} />
        </div>
      ) : null}
    </div>
  );
}

/** The links under a menu item. Scrolled into view, since Settings sits at the bottom of the menu. */
function SubLinks({ links }: { links: { to: string; label: string; icon?: string }[] }) {
  const box = useRef<HTMLDivElement>(null);
  useEffect(() => box.current?.scrollIntoView({ block: "nearest" }), []);
  return (
    <div ref={box} className="ml-[21px] flex flex-none flex-col gap-px border-l border-slate-200 py-0.5 pl-2">
      {links.map((l) => (
        <NavLink key={l.to} to={l.to} className={({ isActive }) => cx("ui-navlink flex h-8 flex-none items-center gap-2 truncate px-2.5 text-[13px]", isActive ? "ui-navlink-on bg-slate-900 font-semibold text-white" : "text-slate-300 hover:bg-slate-800 hover:text-white")}>
          {l.icon ? <Icon name={l.icon} size={15} className="flex-none opacity-70" /> : null}
          <span className="truncate">{l.label}</span>
        </NavLink>
      ))}
    </div>
  );
}

declare const __BUILD_ID__: string;
declare const __APP_VERSION__: string;
/** When this copy of the back office was built (the build id is the build time in base 36). */
const builtAt = () => {
  const t = parseInt(__BUILD_ID__, 36);
  return Number.isFinite(t) && t > 0 ? new Date(t).toLocaleString("en-GB", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : null;
};
function useOnline() {
  const [on, setOn] = useState(typeof navigator === "undefined" ? true : navigator.onLine);
  useEffect(() => {
    const up = () => setOn(true);
    const down = () => setOn(false);
    window.addEventListener("online", up);
    window.addEventListener("offline", down);
    return () => { window.removeEventListener("online", up); window.removeEventListener("offline", down); };
  }, []);
  return on;
}
/** Buildwise: the foot of the side menu. Connection, shops and the version of this back office. */
function SideFoot({ rail, onTip }: { rail: boolean; onTip: (e: React.SyntheticEvent<HTMLElement>, label: string | null) => void }) {
  const online = useOnline();
  const shopsQ = useQuery({ queryKey: ["shops"], queryFn: () => api<{ shops: { id: string; name: string }[] }>(till("/admin/shops")), staleTime: 60_000 });
  const shops = shopsQ.data?.shops.length;
  const when = builtAt();
  const version = `Version ${__APP_VERSION__}`;
  const { store } = useTill();
  const copyright = `© ${new Date().getFullYear()} ${store?.storeName || "Buildwise"}. All rights reserved.`;
  const status = online ? "Online" : "Offline: changes can't be saved";
  if (rail) {
    const label = [status, version, when ? `Updated ${when}` : null, copyright].filter(Boolean).join(" · ");
    return (
      <div className="mt-2 flex flex-none flex-col items-center gap-2 border-t border-slate-800 pt-3">
        <span tabIndex={0} aria-label={label} onMouseEnter={(e) => onTip(e, label)} onMouseLeave={(e) => onTip(e, null)} onFocus={(e) => onTip(e, label)} onBlur={(e) => onTip(e, null)} className="grid h-9 w-9 place-items-center rounded-lg text-[10px] font-bold text-slate-400 outline-none hover:bg-slate-800 focus-visible:ring-2 focus-visible:ring-brand">
          <span className={cx("h-2.5 w-2.5 rounded-full", online ? "bg-emerald-500" : "bg-red-500")} />
        </span>
      </div>
    );
  }
  return (
    <div className="mx-1.5 mt-2 flex flex-none flex-col gap-2 rounded-xl border border-slate-800 bg-slate-800 px-3 py-2.5 text-[12px]">
      <span className="flex items-center justify-between gap-2">
        <span className="flex items-center gap-2 font-semibold"><span className={cx("h-2 w-2 flex-none rounded-full", online ? "bg-emerald-500" : "bg-red-500")} />{online ? "Online" : "Offline"}</span>
        {shops != null ? <span className="text-slate-400">{shops} shop{shops === 1 ? "" : "s"}</span> : null}
      </span>
      {!online ? <span className="text-slate-400">Changes can't be saved until the internet is back.</span> : null}
      <span className="flex flex-col leading-snug text-slate-400">
        <span>{version}</span>
        {when ? <span>Updated {when}</span> : null}
      </span>
      <span className="border-t border-slate-200/70 pt-2 text-[11px] leading-snug text-slate-400">{copyright}</span>
    </div>
  );
}

function Sidebar({ variant, className, onClose }: { variant: "full" | "rail" | "drawer"; className?: string; onClose?: () => void }) {
  const s = useSession();
  const { store, me } = useTill();
  const role: OfficeRole = me?.user.role ?? s.user?.role ?? "owner";
  const shop = useShop();
  const app = useInstall();
  const [subShut, setSubShut] = useState("");
  // Folded menu: a tooltip beside the icon, drawn outside the menu so its scrolling can't clip it.
  const [tip, setTip] = useState<{ label: string; top: number; left: number } | null>(null);
  const showTip = (e: React.SyntheticEvent<HTMLElement>, label: string | null) => {
    if (!label) return setTip(null);
    const r = e.currentTarget.getBoundingClientRect();
    setTip({ label, top: r.top + r.height / 2, left: r.right + 10 });
  };
  useEffect(() => setTip(null), [variant]);

  const rail = variant === "rail";
  const path = useLocation().pathname;
  const products = useQuery({ queryKey: ["admin-items"], queryFn: () => api<{ items: Item[] }>(till("/admin/items")), staleTime: 60_000 });
  const count = products.data?.items.filter((i) => i.active).length;
  const refundsQ = useWaitingRefunds();
  const waitingRefunds = refundsQ.data?.waiting.length ?? 0;
  const name = store?.storeName ?? "Store";
  // The brand's compact logo (no tagline) and mark when the site ships them, else the store's logo.
  const brand = import.meta.env.VITE_BRAND as string | undefined;
  const logo = brand ? `${brand}/logo-compact.png` : store?.settings?.logo;
  return (
    <nav aria-label="Back office" className={cx("ui-side flex h-full min-h-0 flex-col overflow-hidden bg-graphite text-white", rail ? "w-[72px] items-center px-0 py-3.5" : variant === "full" ? "w-[248px] px-3 pt-4 pb-3" : "px-3.5 py-4", className)}>
      {/* Store */}
      <div className={cx("flex items-center gap-3", rail ? "mb-3 flex-none" : "flex-none border-b border-slate-800 px-1.5 pb-4")}>
        {rail && brand ? (
          <img src={`${brand}/mark.png`} alt={name} title={name} className="h-10 w-10 flex-none bg-white object-contain p-1" />
        ) : rail || !logo ? (
          <span className="grid h-10 w-10 flex-none place-items-center bg-brand font-black" title={rail ? name : undefined}>
            {name.trim()[0]?.toUpperCase()}
          </span>
        ) : null}
        {!rail ? (
          <span className="flex min-w-0 flex-1 flex-col gap-1.5">
            {logo ? <img src={brand ? logo : mediaUrl(logo, 600)} alt={name} className="h-7 w-auto max-w-[180px] object-contain object-left" style={brand ? undefined : { filter: "brightness(0) invert(1)" }} /> : <b className="line-clamp-2 text-[13px] leading-tight tracking-wide">{name}</b>}
            <span className="truncate text-[11px] font-semibold uppercase tracking-wider text-slate-400">Shop back office</span>
          </span>
        ) : null}
        {onClose ? (
          <button type="button" aria-label="Close menu" onClick={onClose} className="grid h-10 w-10 flex-none place-items-center text-slate-300">
            <Icon name="x" />
          </button>
        ) : null}
      </div>

      {/* Sections */}
      <div className={cx("ui-navscroll flex min-h-0 flex-1 flex-col overflow-y-auto overscroll-contain", rail ? "mt-2 w-full items-center gap-1.5" : "-mr-1.5 pb-3 pr-1.5")}>
        {GROUPS.map((g) => {
          const items = g.items.filter((i) => (!i.min || atLeast(s.staff?.role, i.min)) && (AREA_OF[i.to] && me?.can ? !!me.can[AREA_OF[i.to]!] : !i.roles || i.roles.includes(role))).map((i) => (i.to === "/stock" && !shop.website && !import.meta.env.VITE_BRAND ? { ...i, label: "Receive & count stock" } : i));
          if (!items.length) return null;
          return (
            <div key={g.label} className={cx("flex flex-col", rail ? "items-center gap-1.5" : "gap-0.5")}>
              {rail ? <span className="my-1.5 h-px w-8 bg-slate-800" /> : <span className="mx-2.5 mb-1 mt-4 flex-none text-[10px] font-bold uppercase tracking-[0.14em] text-slate-500">{g.label}</span>}
              {items.map((i) => (
                <Fragment key={i.to}>
                <NavLink
                  to={i.to}
                  end={i.to === "/"}
                  aria-label={rail ? i.label : undefined}
                  onMouseEnter={rail ? (e) => showTip(e, i.label) : undefined}
                  onMouseLeave={rail ? () => setTip(null) : undefined}
                  onFocus={rail ? (e) => showTip(e, i.label) : undefined}
                  onBlur={rail ? () => setTip(null) : undefined}
                  onClick={rail ? () => setTip(null) : undefined}
                  className={({ isActive: exact }) => { const isActive = exact || (!!import.meta.env.VITE_BRAND && ((i.to === "/products" && isStockPage(path)) || (i.to === "/staff" && isPeoplePage(path)))); return cx("ui-navlink flex items-center gap-2.5 text-sm", rail ? "h-12 w-12 flex-none justify-center" : "h-10 flex-none px-3", isActive ? "ui-navlink-on border-l-4 border-brand-light bg-slate-900 font-semibold text-white" : "border-l-4 border-transparent text-slate-300 hover:bg-slate-800 hover:text-white"); }}
                >
                  {({ isActive }) => (
                    <>
                      <Icon name={i.icon} size={rail ? 20 : 18} />
                      {!rail ? <span className="min-w-0 flex-1 truncate">{i.label}</span> : null}
                      {!rail && i.count === "products" && count != null ? <span className={cx("px-1.5 font-mono text-[11px]", isActive ? "bg-brand-dark" : "bg-slate-800")}>{count}</span> : null}
                      {i.count === "refunds" && waitingRefunds ? <span className={cx("grid min-w-5 place-items-center rounded-full bg-brand px-1.5 text-[11px] font-extrabold leading-5 text-white", rail && "absolute ml-6 -mt-6")}>{waitingRefunds}</span> : null}
                      {!rail && i.subs ? (
                        <span role="button" tabIndex={0} aria-label={subShut === i.to || !path.startsWith(i.to) ? `Show ${i.label} pages` : `Hide ${i.label} pages`} onClick={(e) => { if (!path.startsWith(i.to)) return; e.preventDefault(); e.stopPropagation(); setSubShut((v) => (v === i.to ? "" : i.to)); }} onKeyDown={(e) => { if ((e.key === "Enter" || e.key === " ") && path.startsWith(i.to)) { e.preventDefault(); e.stopPropagation(); setSubShut((v) => (v === i.to ? "" : i.to)); } }} className="-mr-1 grid h-7 w-7 flex-none place-items-center rounded-md hover:bg-white/10">
                          <Icon name="chev" size={15} className={cx("transition-transform", path.startsWith(i.to) && subShut !== i.to ? "rotate-90" : "")} />
                        </span>
                      ) : null}
                    </>
                  )}
                </NavLink>
                {!rail && i.subs && path.startsWith(i.to) && subShut !== i.to ? <SubLinks links={i.subs} /> : null}
                </Fragment>
              ))}
            </div>
          );
        })}
      </div>

      {import.meta.env.VITE_BRAND && variant !== "drawer" ? <>
        <SideFoot rail={rail} onTip={showTip} />
        {rail && tip ? createPortal(
          <div role="tooltip" style={{ top: tip.top, left: tip.left }} className="pointer-events-none fixed z-[80] -translate-y-1/2 whitespace-nowrap rounded-lg bg-ink px-2.5 py-1.5 text-[12.5px] font-semibold text-white shadow-[0_8px_24px_rgba(11,21,23,0.25)]">
            <span className="absolute -left-1 top-1/2 h-2 w-2 -translate-y-1/2 rotate-45 bg-ink" />
            {tip.label}
          </div>,
          document.body,
        ) : null}
      </> : <>
      {/* Install as an app (Chrome / Edge) */}
      {app.canPrompt && !rail ? (
        <button type="button" onClick={() => void app.install()} className="mx-1.5 mt-2.5 flex h-10 flex-none items-center justify-center gap-2 rounded-[10px] bg-brand text-[13px] font-bold text-white hover:bg-brand-dark">
          Install as an app
        </button>
      ) : null}

      {/* Signed in */}
      <div className={cx("mt-2.5 flex flex-none items-center gap-2.5", rail ? "flex-col pt-3" : "border-t border-slate-800 px-1.5 pt-3")}>
        <span title={rail ? `${s.user?.name} · ${s.user?.email}` : undefined}>
          <Avatar name={s.user?.name ?? "?"} color="#9fe0e3" size={34} />
        </span>
        {!rail ? (
          <span className="flex min-w-0 flex-1 flex-col">
            <b className="truncate text-[13px]">{s.user?.name}</b>
            <span className="truncate text-[11px] text-slate-400">{me?.user.roleLabel ?? OFFICE_ROLE_LABEL[role]} · {s.user?.email}</span>
          </span>
        ) : null}
        <button type="button" aria-label="Log out" title="Log out" onClick={() => session.signOut()} className={cx("flex h-9 flex-none items-center justify-center gap-1.5 bg-slate-800 text-[12px] font-semibold text-slate-300 hover:text-white", rail ? "w-9" : "px-2.5")}>
          <Icon name="logout" size={16} />
          {rail ? null : "Log out"}
        </button>
      </div>
      </>}
    </nav>
  );
}

export function PageHead({ title, sub, children }: { title: string; sub?: string; children?: React.ReactNode }) {
  return (
    <div className="mb-5 flex flex-wrap items-end justify-between gap-3">
      <div className="min-w-0">
        <h1 className="ui-pagetitle text-3xl font-black tracking-tight sm:text-[34px]">{title}</h1>
        {sub ? <p className="ui-pagesub mt-1 text-sm text-slate-500">{sub}</p> : null}
      </div>
      {children ? <div className="flex flex-wrap gap-2">{children}</div> : null}
    </div>
  );
}

export function Card({ title, right, children, className }: { title?: string; right?: React.ReactNode; children: React.ReactNode; className?: string }) {
  return (
    <section className={cx("border border-slate-200 bg-white", className)}>
      {title ? (
        <div className="flex flex-wrap items-baseline justify-between gap-2 px-4 pb-2 pt-4 sm:px-5">
          <h2 className="text-base font-bold">{title}</h2>
          {right}
        </div>
      ) : null}
      {children}
    </section>
  );
}

export const th = "whitespace-nowrap border-b border-slate-100 px-3.5 py-2.5 text-left text-xs font-semibold text-slate-500";
export const td = "whitespace-nowrap border-b border-slate-50 px-3.5 py-3 text-sm";
